// Remanejamento de visitas/rotas do módulo de campo (Motoboy v3).
// Centraliza a lógica usada pelo operador (OperadorView) e pelo gestor (RotasView):
// mover a tarefa de dia, sair da rota de origem, anexar/criar rota no destino e
// registrar o histórico auditável em visitas_remanejamentos (painel do líder).
import { supabase } from './supabase';

export interface RemanejamentoLog {
    rota_origem_id?: string | null;
    rota_destino_id?: string | null;
    parada_id?: string | null;
    tarefa_id?: number | null;
    negociacao_id?: number | null;
    user_id: string;
    data_original: string; // YYYY-MM-DD
    nova_data: string | null; // NULL = mantida pendente
    motivo?: string | null;
    origem: 'operador' | 'gestor' | 'alerta';
}

/** Grava uma ou mais linhas no histórico de remanejamentos (não lança — auditoria não bloqueia o fluxo). */
export const registrarRemanejamentos = async (logs: RemanejamentoLog[]) => {
    if (logs.length === 0) return;
    const { error } = await supabase.from('visitas_remanejamentos').insert(logs.map(l => ({
        rota_origem_id: l.rota_origem_id ?? null,
        rota_destino_id: l.rota_destino_id ?? null,
        parada_id: l.parada_id ?? null,
        tarefa_id: l.tarefa_id ?? null,
        negociacao_id: l.negociacao_id ?? null,
        user_id: l.user_id,
        data_original: l.data_original,
        nova_data: l.nova_data,
        motivo: l.motivo || null,
        origem: l.origem,
    })));
    if (error) console.error('Erro ao registrar remanejamento:', error);
};

/** Localiza (ou cria) a rota do vendedor na data destino para receber paradas remanejadas. */
export const obterRotaDestino = async (vendedorId: string, novaData: string): Promise<{ id: string; proxPos: number }> => {
    const { data: rotaExistente } = await supabase
        .from('rotas_comerciais')
        .select('id, rota_paradas(id)')
        .eq('vendedor_id', vendedorId)
        .eq('data', novaData)
        .in('status', ['planejada', 'agendada', 'reagendada', 'em_execucao'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    if (rotaExistente) {
        return { id: rotaExistente.id as string, proxPos: ((rotaExistente as any).rota_paradas?.length || 0) + 1 };
    }
    const { data: nova, error } = await supabase
        .from('rotas_comerciais')
        .insert([{ vendedor_id: vendedorId, data: novaData, origem: 'manual', status: 'planejada' }])
        .select('id')
        .single();
    if (error) throw error;
    return { id: nova.id as string, proxPos: 1 };
};

export interface ParadaRemanejavel {
    id: string;
    tarefa_id?: number | null;
    negociacao_id?: number | null;
}

interface RemanejarParadasArgs {
    paradas: ParadaRemanejavel[];
    rotaOrigemId: string;
    vendedorId: string;
    dataOriginal: string;
    novaData: string;
    motivo?: string | null;
    userId: string;
    origem: 'operador' | 'gestor' | 'alerta';
}

/**
 * Remaneja leads específicos para outro dia: move a tarefa de vencimento,
 * tira a parada da rota de origem e anexa na rota do destino (criando se preciso).
 */
export const remanejarParadas = async ({ paradas, rotaOrigemId, vendedorId, dataOriginal, novaData, motivo, userId, origem }: RemanejarParadasArgs) => {
    if (paradas.length === 0) return;
    const destino = await obterRotaDestino(vendedorId, novaData);

    const tarefaIds = paradas.map(p => p.tarefa_id).filter(Boolean) as number[];
    if (tarefaIds.length > 0) {
        const { error } = await supabase.from('tarefas').update({ vencimento: novaData }).in('id', tarefaIds);
        if (error) throw error;
    }

    // Recria as paradas no destino preservando lead/tarefa e remove da origem
    const novas = paradas.map((p, i) => ({
        rota_id: destino.id,
        posicao: destino.proxPos + i,
        negociacao_id: p.negociacao_id ?? null,
        tarefa_id: p.tarefa_id ?? null,
    }));
    const { error: insErr } = await supabase.from('rota_paradas').insert(novas);
    if (insErr) throw insErr;
    const { error: delErr } = await supabase.from('rota_paradas').delete().in('id', paradas.map(p => p.id));
    if (delErr) throw delErr;

    await registrarRemanejamentos(paradas.map(p => ({
        rota_origem_id: rotaOrigemId,
        rota_destino_id: destino.id,
        parada_id: p.id,
        tarefa_id: p.tarefa_id ?? null,
        negociacao_id: p.negociacao_id ?? null,
        user_id: userId,
        data_original: dataOriginal,
        nova_data: novaData,
        motivo,
        origem,
    })));
};

interface RemanejarRotaArgs {
    rotaId: string;
    dataOriginal: string;
    novaData: string;
    motivo?: string | null;
    userId: string;
    origem: 'operador' | 'gestor' | 'alerta';
}

/** Altera a data de uma rota inteira (paradas ainda não visitadas acompanham). */
export const remanejarRotaInteira = async ({ rotaId, dataOriginal, novaData, motivo, userId, origem }: RemanejarRotaArgs) => {
    const { data: paradas, error: parErr } = await supabase
        .from('rota_paradas')
        .select('id, status, tarefa_id, negociacao_id')
        .eq('rota_id', rotaId);
    if (parErr) throw parErr;

    const { error } = await supabase.from('rotas_comerciais')
        .update({ data: novaData, status: 'reagendada' })
        .eq('id', rotaId);
    if (error) throw error;

    const pendentes = (paradas || []).filter(p => p.status !== 'visitado');
    const tarefaIds = pendentes.map(p => p.tarefa_id).filter(Boolean) as number[];
    if (tarefaIds.length > 0) {
        await supabase.from('tarefas').update({ vencimento: novaData }).in('id', tarefaIds);
    }

    await registrarRemanejamentos(pendentes.map(p => ({
        rota_origem_id: rotaId,
        rota_destino_id: rotaId,
        parada_id: p.id as string,
        tarefa_id: (p.tarefa_id as number) ?? null,
        negociacao_id: (p.negociacao_id as number) ?? null,
        user_id: userId,
        data_original: dataOriginal,
        nova_data: novaData,
        motivo,
        origem,
    })));
};

/** Registra a decisão "manter pendente" do alerta de fim de expediente (nova_data = NULL). */
export const registrarManterPendentes = async (
    paradas: ParadaRemanejavel[],
    rotaId: string,
    dataOriginal: string,
    userId: string,
) => {
    await registrarRemanejamentos(paradas.map(p => ({
        rota_origem_id: rotaId,
        rota_destino_id: null,
        parada_id: p.id,
        tarefa_id: p.tarefa_id ?? null,
        negociacao_id: p.negociacao_id ?? null,
        user_id: userId,
        data_original: dataOriginal,
        nova_data: null,
        motivo: 'Mantidas pendentes na data original (alerta de fim de expediente)',
        origem: 'alerta',
    })));
};
